<template>
    <div v-if="estimate">
        <header class="bg-gray-100 text-gray-700">
            <div class="w-full mx-auto p-8 lg:p-12 flex flex-col md:flex-row-reverse" style="max-width: 1800px">
                <div class="md:w-72 mb-12 md:mb-0 md:ml-8 lg:ml-12">
                    <img src="https://insync.software/mobile-img/logo.svg" class="h-12">
                    <div class="mt-4">
                        <p class="font-semibold mb-1">Hambro Roofing Limited</p>
                        <p>Unit 2, 6-10 Lamson Road Rainham Essex RM13 9YY</p>
                        <p><a href="tel:01708803930">01708 803 930</a></p>
                        <p><a href="http://www.hambroroofing.com">www.hambroroofing.com</a></p>
                    </div>
                </div>
                <div class="flex-1">
                    <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div class="text-xl">
                            <h2 class="font-semibold text-gray-700">Client Address:</h2>
                            <p class="mt-1 text-gray-500">{{ estimate.summaryAddress }}</p>
                        </div>
                        <div class="text-xl">
                            <div class="text-gray-500">
                                <div class="inline-block font-semibold text-gray-700 mr-2">Date:</div>
                                {{ estimate.summaryDate }}
                            </div>
                            <div class="text-gray-500">
                                <div class="inline-block font-semibold text-gray-700 mr-2">Valid Until:</div>
                                {{ estimate.summaryValidUntil }}
                            </div>
                        </div>
                        <div class="text-xl">
                            <h2 class="font-semibold text-gray-700">Package Address:</h2>
                            <p class="mt-1 text-gray-500">{{ estimate.packageAddress }}</p>
                        </div>
                        <div class="text-xl">
                            <h2 class="font-semibold text-gray-700">Prepared By:</h2>
                            <p class="mt-1 text-gray-500">{{ estimate.ownerName }}</p>
                            <p class="mt-1 text-gray-500 font-semibold"><a
                                :href="'mailto:'+estimate.ownerEmail">{{ estimate.ownerEmail }}</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <main class="bg-white text-gray-700">
            <div class="w-full mx-auto p-8 lg:p-12 pb-16 lg:pb-16 flex flex-col lg:flex-row-reverse"
                 style="max-width: 1800px">
                <div class="lg:w-72 mb-12 lg:mb-0 lg:ml-12 ">
                    <div class="space-y-2 sticky top-0">
                        <div class="text-left sm:text-right lg:text-left justify-center flex" @click="download">
                            <div
                                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 hover:text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
                                <i class="fas fa-file-pdf mr-2"></i>
                                Download PDF
                            </div>
                        </div>
                        <div class="text-left sm:text-right lg:text-left justify-center flex mb-6" @click="downloadXLSX">
                            <div
                                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 hover:text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
                                <i class="fas fa-file-excel mr-2"></i>
                                Download as Excel XLSX
                            </div>
                        </div>

                        <div>
                            <h2 class="text-lg font-semibold text-gray-700 mt-1">Collection</h2>
                            <div class="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <table class="w-full table-fixed">
                                    <tbody>
                                    <tr v-for="section in estimate.scopeOfWorks.filter((i) => !i.optional)">
                                        <td class="align-top text-gray-600 text-sm">{{ section.title }}</td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency(Math.round(section.total * 100) / 100, false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="align-top text-gray-600 text-sm font-semibold w-auto whitespace-nowrap">Scope of works total
                                        </td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-bold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency(Math.round(estimate.scopeOfWorks.filter((i) => !i.optional).reduce((a, section) => a + section.total, 0) * 100) / 100, false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colspan="2">
                                            <div class="border-t border-gray-200 my-3"></div>
                                        </td>
                                    </tr>
                                    <tr v-if="estimate.adjustment">
                                        <td class="align-top text-gray-600 text-sm">Adjustment</td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency(0 - Math.round(estimate.netAdjustment * 100) / 100, false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="estimate.mcd">
                                        <td class="align-top text-gray-600 text-sm">MCD</td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency(0 - Math.round(estimate.netMCD * 100) / 100, false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="estimate.preliminaries.length">
                                        <td class="align-top text-gray-600 text-sm">Preliminaries</td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency(Math.round(estimate.preliminaries.reduce((a, b) => a + parseFloat(b.total.replace(',', '').replace('£', '')), 0) * 100) / 100, false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="estimate.preliminaries.length">
                                        <td colspan="2">
                                            <div class="border-t border-gray-200 my-3"></div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="align-top text-gray-600 text-sm font-semibold">Total</td>
                                        <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-bold">
                                            <div class="flex items-center justify-between">
                                                <div>£</div>
                                                <div>{{
                                                        toCurrency((Math.round(estimate.netTotal * 100) / 100) + (Math.round(estimate.preliminaries.reduce((a, b) => a + parseFloat(b.total.replace(',', '').replace('£', '')), 0) * 100) / 100), false)
                                                    }}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <template v-if="estimate.scopeOfWorks.some((i) => i.optional)">
                                        <tr>
                                            <td colspan="2" class="pt-3">
                                                <div class="relative">
                                                    <div class="border-t border-gray-200 my-3"></div>
                                                    <div class="absolute inset-0 flex items-center justify-center">
                                                        <div
                                                            class="text-xs px-2 py-1 bg-gray-50 text-gray-400 font-semibold">
                                                            Options
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-for="section in estimate.scopeOfWorks.filter((i) => i.optional)">
                                            <td class="align-top text-gray-600 text-sm">{{ section.title }}</td>
                                            <td class="align-top text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                                <div class="flex items-center justify-between">
                                                    <div>£</div>
                                                    <div>{{
                                                            toCurrency(Math.round(section.total * 100) / 100, false)
                                                        }}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colspan="2">
                                                <div class="pt-3 text-xs bg-gray-50 text-gray-400 italic">
                                                    Options are excluded from the total above.
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex-1">
                    <div class="text-5xl font-semibold text-gray-900">{{ estimate.title }}</div>
                    <div class="mb-8 text-gray-500"><span class="font-semibold">Reference #{{
                            estimate.reference
                        }}</span> • {{ estimate.package }}
                    </div>
                    <div class="text-gray-700">
                        <p>
                            Dear {{ estimate.summaryTo ? estimate.summaryTo : 'Sir/Madam' }},
                        </p>
                        <template v-for="item in estimate.summaryParagraphs">
                            <template v-if="item.content">
                                <h3 class="font-semibold mt-5 text-gray-700">{{ item.title }}</h3>
                                <p class="mt-1 whitespace-pre-wrap">{{ item.content }}</p>
                            </template>
                        </template>

                        <div class="mt-5" v-if="estimate.emailSignature">
                            <div class="whitespace-pre-wrap" v-text="estimate.emailSignature"></div>
                        </div>
                    </div>
                    <div>
                        <template v-for="section in estimate.scopeOfWorks.filter((i) => !i.optional)">
                            <estimate-external-section-view :estimate="estimate"
                                                            :section="section"></estimate-external-section-view>
                        </template>
                        <table class="w-full mt-2 text-gray-600">
                            <tbody>
                            <tr class="hidden md:table-row" v-if="estimate.adjustment">
                                <td class="align-top text-right py-1 whitespace-pre-wrap"
                                    v-text="estimate.adjustmentComment"
                                ></td>
                                <td class="text-right text-sm font-semibold text-gray-500 py-1 pr-2" width="140">{{
                                        0 - Math.round(estimate.netAdjustment * 100) / 100 | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr class="hidden md:table-row" v-if="estimate.mcd">
                                <td class="align-top text-right py-2 whitespace-pre-wrap">MCD Discount</td>
                                <td class="text-right text-sm font-semibold text-gray-500 py-1 pr-2" width="140">{{
                                        0 - Math.round(estimate.netMCD * 100) / 100 | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr class="md:hidden" v-if="estimate.adjustment">
                                <td class="text-left text-sm font-semibold text-gray-500 py-1 pl-2">
                                    {{ estimate.adjustmentComment }}
                                </td>
                                <td class="text-right text-sm font-semibold text-gray-500 py-1 pr-2" width="140">{{
                                        0 - Math.round(estimate.netAdjustment * 100) / 100 | toCurrency
                                    }}
                                </td>
                            </tr>
                            <tr class="md:hidden" v-if="estimate.mcd">
                                <td class="text-left text-sm font-semibold text-gray-500 py-1 pl-2">MCD</td>
                                <td class="text-right text-sm font-semibold text-gray-500 py-1 pr-2" width="140">{{
                                        0 - Math.round(estimate.netMCD * 100) / 100 | toCurrency
                                    }}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <div class="my-16 bg-gray-50 border border-gray-200 rounded-xl p-4"
                             v-if="estimate.scopeOfWorks.some((i) => i.optional)">
                            <h2 class="text-2xl text-gray-700 font-semibold">Optional items</h2>
                            <p class="mt-1 text-gray-600">The following are optional items and are not included in the
                                total sum of the estimate.</p>
                        </div>

                        <template v-for="section in estimate.scopeOfWorks.filter((i) => i.optional)">
                            <estimate-external-section-view :estimate="estimate"
                                                            :section="section"></estimate-external-section-view>
                        </template>

                        <div class="my-16 bg-gray-50 border border-gray-200 rounded-xl p-4"
                             v-if="estimate.preliminaries.length">
                            <h2 class="text-2xl text-gray-700 font-semibold">Preliminary items</h2>
                            <p class="mt-1 text-gray-600">The following are preliminary items.</p>
                        </div>

                        <estimate-external-preliminary-view :estimate="estimate"
                                                            :preliminaries="estimate.preliminaries"></estimate-external-preliminary-view>

                    </div>
                    <div class="text-center mt-16 text-gray-500 italic">
                        <p>
                            All Prices ex VAT at current rate.
                        </p>
                        <p>
                            This quote is subject to our standard
                            <a href="https://insync.software/terms/quotes"
                               class="text-brand hover:text-brand hover:underline">
                                Terms & Conditions.
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import {ExternalEstimate} from '@models'
import moment from "moment";
import EstimateExternalSectionView from "./EstimateExternalSectionView.vue";
import {toCurrency} from "../../../../utils";
import EstimateExternalPreliminaryView from "./EstimateExternalPreliminaryView.vue";

export default {
    components: {EstimateExternalPreliminaryView, EstimateExternalSectionView},
    props: {
        id: null | String,
    },
    data() {
        return {
            estimate: null,
        }
    },
    computed: {},
    methods: {
        toCurrency,
        print(url) {
            if (!this.iframe) {
                this.iframe = document.createElement('iframe');
                this.iframe.setAttribute('style', 'display:none');
                this.iframe.src = "";
                this.iframe.name = 'pdf';
                this.iframe.onload = () => {
                    this.$nextTick(() => {
                            setTimeout(() => {
                                const pdfFrame = window.frames["pdf"];
                                pdfFrame.focus();
                                pdfFrame.print();
                            }, 100)
                        }
                    )
                }
                document.body.appendChild(this.iframe);
            }
            this.iframe.src = url + '?time=' + moment().format('x')
        },
        download() {
            window.location.href = this.estimate.getDownloadUrl()
        },
        downloadXLSX() {
            window.location.href = this.estimate.getDownloadXLSXUrl()
        }
    },
    async mounted() {
        if (this.id) {
            this.estimate = await ExternalEstimate.find(this.id);
            if (this.estimate) {
                setTimeout(() => {
                    this.estimate.markViewed(this.estimate.id);
                }, 200)
            }
        }
    },
}
</script>
